import { InboxOutlined, PlusOutlined } from "@ant-design/icons";
import { App, Button, Form, Input, Modal, Pagination, Spin, Table, Tag } from "antd";
import { useEffect, useState } from "react";
import {
	DataTableCard,
	EmptyState,
	PageHeader,
	RowActions,
	SearchInput,
} from "@/shared/components/ui";
import { useTableState } from "@/shared/hooks/useTableState";
import { isConflictError } from "@/shared/lib/api-error";
import { formatDate } from "@/shared/lib/format";
import { useCreateItem, useDeleteItem, useItemsList, useUpdateItem } from "../hooks/useItems";
import type { Item } from "../types";

interface ItemsMobileListProps {
	items: Item[];
	isLoading: boolean;
	search: string;
	total: number;
	page: number;
	limit: number;
	onPageChange: (page: number, pageSize: number) => void;
	openCreate: () => void;
	openEdit: (item: Item) => void;
	onDelete: (id: string) => void;
	deleting: boolean;
}

function ItemsMobileList({
	items,
	isLoading,
	search,
	total,
	page,
	limit,
	onPageChange,
	openCreate,
	openEdit,
	onDelete,
	deleting,
}: ItemsMobileListProps) {
	if (isLoading) {
		return (
			<div className="flex justify-center p-8">
				<Spin />
			</div>
		);
	}

	if (items.length === 0) {
		return (
			<div className="p-4">
				<EmptyState
					title={search ? "Hech narsa topilmadi" : "Mahsulotlar yo'q"}
					description={
						search
							? "Boshqa nom bilan qidirib ko'ring."
							: "Yordam sifatida tarqatiladigan mahsulotlarni qo'shing."
					}
					icon={<InboxOutlined />}
					action={
						search ? null : (
							<Button icon={<PlusOutlined />} onClick={openCreate}>
								Mahsulot qo'shish
							</Button>
						)
					}
				/>
			</div>
		);
	}

	return (
		<div>
			<div className="divide-y divide-[var(--c-border-subtle)]">
				{items.map((item) => (
					<div key={item.id} className="flex items-center gap-3 p-3.5">
						<div className="min-w-0 flex-1">
							<div className="truncate text-sm font-medium text-[var(--c-text)]">{item.name}</div>
							<div className="mt-0.5">
								<Tag bordered={false}>{item.unit}</Tag>
							</div>
						</div>

						<RowActions
							alwaysVisible
							onEdit={() => openEdit(item)}
							onDelete={() => onDelete(item.id)}
							deleteTitle="Mahsulot o'chirilsinmi?"
							deleting={deleting}
						/>
					</div>
				))}
			</div>

			{total > limit && (
				<div className="flex justify-center border-t border-[var(--c-border-subtle)] p-3">
					<Pagination
						simple
						current={page}
						pageSize={limit}
						total={total}
						onChange={onPageChange}
					/>
				</div>
			)}
		</div>
	);
}

export function ItemsPage() {
	const { message } = App.useApp();
	const { page, limit, search, onSearch, onPageChange } = useTableState();
	const { data, isLoading, isFetching } = useItemsList({ page, limit, name: search || undefined });

	const [form] = Form.useForm<{ name: string; unit: string }>();
	const [modalOpen, setModalOpen] = useState(false);
	const [editing, setEditing] = useState<Item | null>(null);

	const createMutation = useCreateItem();
	const updateMutation = useUpdateItem();
	const deleteMutation = useDeleteItem();

	useEffect(() => {
		if (modalOpen) {
			form.setFieldsValue({ name: editing?.name ?? "", unit: editing?.unit ?? "" });
		}
	}, [modalOpen, editing, form]);

	const openCreate = () => {
		setEditing(null);
		setModalOpen(true);
	};

	const openEdit = (item: Item) => {
		setEditing(item);
		setModalOpen(true);
	};

	const handleSubmit = async () => {
		const values = await form.validateFields();

		if (editing) {
			updateMutation.mutate(
				{ id: editing.id, data: values },
				{ onSuccess: () => setModalOpen(false) }
			);
		} else {
			createMutation.mutate(values, { onSuccess: () => setModalOpen(false) });
		}
	};

	const handleDelete = (id: string) => {
		deleteMutation.mutate(id, {
			onError: (error) => {
				message.error(
					isConflictError(error)
						? "O'chirib bo'lmadi: mahsulot ishlatilmoqda"
						: "O'chirishda xatolik yuz berdi"
				);
			},
		});
	};

	const total = data?.meta.total ?? 0;
	const items = data?.items ?? [];
	const isDataLoading = isLoading || isFetching;

	return (
		<div>
			<PageHeader
				title="Mahsulotlar"
				description="Tarqatiladigan mahsulotlar va o'lchov birliklari"
				actions={
					<Button
						type="primary"
						icon={<PlusOutlined />}
						onClick={openCreate}
						className="w-full sm:w-auto"
					>
						Mahsulot qo'shish
					</Button>
				}
			/>

			<DataTableCard
				toolbar={
					<SearchInput
						placeholder="Nomi bo'yicha qidirish…"
						onSearch={onSearch}
						className="w-full sm:w-64"
					/>
				}
				toolbarExtra={total ? `${total} ta yozuv` : null}
			>
				{/* Desktop table */}
				<div className="hidden sm:block">
					<Table<Item>
						rowKey="id"
						loading={isDataLoading}
						dataSource={items}
						scroll={{ x: "max-content" }}
						locale={{
							emptyText: (
								<EmptyState
									title={search ? "Hech narsa topilmadi" : "Mahsulotlar yo'q"}
									description={
										search
											? "Boshqa nom bilan qidirib ko'ring."
											: "Yordam sifatida tarqatiladigan mahsulotlarni qo'shing."
									}
									icon={<InboxOutlined />}
									action={
										search ? null : (
											<Button icon={<PlusOutlined />} onClick={openCreate}>
												Mahsulot qo'shish
											</Button>
										)
									}
								/>
							),
						}}
						pagination={
							total > limit
								? {
										current: page,
										pageSize: limit,
										total,
										showSizeChanger: true,
										onChange: onPageChange,
										className: "!px-4",
									}
								: false
						}
						columns={[
							{
								title: "Nomi",
								dataIndex: "name",
								render: (value: string) => <span className="font-medium">{value}</span>,
							},
							{
								title: "O'lchov birligi",
								dataIndex: "unit",
								width: 160,
								render: (value: string) => <Tag bordered={false}>{value}</Tag>,
							},
							{
								title: "Yaratilgan",
								dataIndex: "createdAt",
								width: 160,
								responsive: ["md"],
								render: (value: string) => (
									<span className="numeric text-[var(--c-text-secondary)]">
										{formatDate(value)}
									</span>
								),
							},
							{
								title: "",
								key: "actions",
								width: 88,
								align: "right",
								fixed: "right",
								render: (_, record) => (
									<RowActions
										onEdit={() => openEdit(record)}
										onDelete={() => handleDelete(record.id)}
										deleteTitle="Mahsulot o'chirilsinmi?"
										deleting={deleteMutation.isPending}
									/>
								),
							},
						]}
					/>
				</div>

				{/* Mobile cards */}
				<div className="block sm:hidden">
					<ItemsMobileList
						items={items}
						isLoading={isDataLoading}
						search={search}
						total={total}
						page={page}
						limit={limit}
						onPageChange={onPageChange}
						openCreate={openCreate}
						openEdit={openEdit}
						onDelete={handleDelete}
						deleting={deleteMutation.isPending}
					/>
				</div>
			</DataTableCard>

			<Modal
				title={editing ? "Mahsulotni tahrirlash" : "Yangi mahsulot"}
				open={modalOpen}
				onCancel={() => setModalOpen(false)}
				onOk={handleSubmit}
				okText={editing ? "Saqlash" : "Qo'shish"}
				cancelText="Bekor qilish"
				confirmLoading={createMutation.isPending || updateMutation.isPending}
				destroyOnHidden
			>
				<Form form={form} layout="vertical" className="mt-4" requiredMark={false}>
					<Form.Item
						name="name"
						label="Nomi"
						rules={[{ required: true, message: "Nomi kiritilishi shart" }]}
					>
						<Input placeholder="masalan: Un" autoFocus />
					</Form.Item>
					<Form.Item
						name="unit"
						label="O'lchov birligi"
						rules={[{ required: true, message: "O'lchov birligi kiritilishi shart" }]}
						extra="Masalan: kg, dona, litr"
					>
						<Input placeholder="kg" />
					</Form.Item>
				</Form>
			</Modal>
		</div>
	);
}
